import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import AuthContext from './AuthContext'; // Import AuthContext
import LoadingSpinner from './LoadingSpinner';
import './CustomerForm.css';

const CustomerForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext); // Use AuthContext for user claims
  const { customerId, dataClient } = useOutletContext(); // Get context from CustomerLayout
  const [error, setError] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const toBool = (v) => {
    if (v === true) return true;
    if (v === false || v == null) return false;

    // numbers: 0/1/etc
    if (typeof v === "number") return v !== 0;

    // strings: "0", "1", "true", "false", "Y", etc
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "1" || s === "true" || s === "y" || s === "yes" || s === "on";
    }

    return false;
  };
  const handleBlurTrim = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: typeof value === "string" ? value.trim() : value,
    }));
  };

  useEffect(() => {
      // if no user in context, log them in again
      if (!user) {
        navigate('/login');
        return;
      }
      // check expiration
      const currentTime = Date.now() / 1000; // in seconds
      if (user.exp && user.exp <= currentTime) {
        // token has expired
        navigate('/login');
      }
    }, [user, navigate]);

  const [formData, setFormData] = useState({
    customer_number: '',
    customer_name: '',
    home_page: '',
    limit_access: '',
    title_graphic: '',
    products: '',
    greeting: '',
    pay_tax_message: '',
    disable_message: '',
    county_number: '',
    accept_delinquents: '1',
    current_year_delinquents: false,
    import_payment: false,
    new_experience: false,
    disable_list: '',
    ip_access_list: '',
    taxid_block_list: '',
    paydelq: '',
    active: '',
    uid: '',
    tax_search_message: '',
    payment_gateway: ''
  });

  const paymentGatewayLabels = {
    nic: 'NIC/Montana Interactive',
    certpay: 'Certified Payments',
    opi: 'Official Payments',
    nmi: 'Network Merchants',
    gti: 'GovTeller / Intellipay',
    nue: 'Nuevie'
  };

  // Fetch customer data if customerId is provided or initialize with dataClient
  useEffect(() => {
    // 1. Check if user is missing
    if (!user) {
      setError('User is not authenticated.');
      navigate('/login');
      return;
    }
  
    // 2. Check token expiration
    const currentTime = Date.now() / 1000; // in seconds
    if (user.exp && user.exp <= currentTime) {
      navigate('/login');
      return;
    }
  
    // 3. Check user role
    if (user.role_name === 'User') {
      setError('Access denied.');
      navigate('/unauthorized');
      return;
    }
  
    // 4. Fetch data (if needed)
    if (customerId) {
      setIsLoading(true);
      fetchCustomerData(customerId)
        .then((data) => {
          if (data.name) {
            data.customer_name = data.name;
            delete data.name;
          }
          setFormData((prevData) => ({ ...prevData, ...data,
            current_year_delinquents: toBool(data.current_year_delinquents),
            import_payment: toBool(data.import_payment)
           }));
          setError(null);
        })
       .catch((err) => {
          console.error('Failed to fetch customer data:', err);
          if (err.message === 'NotFound') {
            navigate('/not-found');
          } else {
            setError('Failed to load customer data.');
          }
        })


        .finally(() => setIsLoading(false));
    } else if (dataClient) {
      setFormData((prevData) => ({ ...prevData, ...dataClient,
        current_year_delinquents: toBool(dataClient.current_year_delinquents),
        import_payment: toBool(dataClient.import_payment)
       }));
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [customerId, dataClient, user, navigate]);

  const fetchCustomerData = async (customerId) => {
    const response = await fetch(`${process.env.REACT_APP_PROXY_URL}/api/customer/manage/${customerId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.status === 404) {
      throw new Error('NotFound');
    }

    //if (!response.ok) {
      //throw new Error('Failed to fetch customer data');
    //}

    return await response.json();
  };


  const handleSubmit = async (e) => {    
    e.preventDefault();
    try {
      const url = customerId
        ? `${process.env.REACT_APP_PROXY_URL}/api/client/update`
        : `${process.env.REACT_APP_PROXY_URL}/api/client/create`;
        const payload = {
          ...formData,
          title_graphic: formData.title_graphic.trim(),
          id: customerId || null,
          import_payment: Boolean(formData.import_payment), // ✅ Ensures Boolean
          current_year_delinquents: Boolean(formData.current_year_delinquents),          
        };

        console.log('Submitting payload:', payload);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        });      

      if (!response.ok) {
        throw new Error('Failed to submit customer data');
      }

      const result = await response.json();
      setResponseMessage('Customer saved successfully');
      navigate(`/customer/${customerId || 'new'}/client/list`);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('An error occurred while submitting the form.');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="error_message">{error}</div>;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? Boolean(checked) : value,
    }));
  };
  
  // PRODUCT LIST FUNCTIONALITY
  const PRODUCT_OPTIONS = {
    tax: 'Web Tax',
    doc: 'Web Doc',
    tpay: 'Web Pay'
  };
  
  const handleProductChange = (e) => {
    const { value, checked } = e.target;
    const currentList = formData.products
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  
    let updatedList = new Set(currentList);
    if (checked) {
      updatedList.add(value);
    } else {
      updatedList.delete(value);
    }
  
    setFormData((prevData) => ({
      ...prevData,
      products: Array.from(updatedList).join(','),
    }));
  };
  
  const hasProduct = (key) =>
    formData.products
      .split(',')
      .map((v) => v.trim())
      .includes(key);
  // END OF PRODUCT LIST FUNCTIONALITY
  
  // DISABLE LIST FUNCTIONALITY
  const DISABLE_OPTIONS = {
    tax: 'Web Tax',
    doc: 'Web Doc',
    tpay: 'Web Pay'
  };
  
  const handleDisableChange = (e) => {
    const { value, checked } = e.target;
    const currentList = formData.disable_list
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  
    let updatedList = new Set(currentList);
    if (checked) {
      updatedList.add(value);
    } else {
      updatedList.delete(value);
    }
  
    setFormData((prevData) => ({
      ...prevData,
      disable_list: Array.from(updatedList).join(','),
    }));
  };
  
  const isDisabled = (key) =>
    formData.disable_list
      .split(',')
      .map((v) => v.trim())
      .includes(key);
  //  END OF DISABLE LIST FUNCTIONALITY

  return (
    <div className="overlay override">
      {responseMessage && (
        <div className="response_status_message" id="response_message">
          <span>{responseMessage}</span>
        </div>
      )}
      {error && <div className="error_message">{error}</div>}
      <form id="bms_edit_customer" onSubmit={handleSubmit}>
        <div className="data_entry_block" id="customer_entry_block">
          <table border="0">
            <tbody>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Customer Number
                </th>
                <td>
                  {user.role_name === 'Admin' && !customerId ? (
                    <input
                      name="customer_number"
                      type="text"
                      className="data_entry_field"
                      value={formData.customer_number}
                      size="10"
                      onChange={handleChange}
                    />
                  ) : (
                    <>
                      {formData.customer_number} 
                      <input
                        name="customer_number"
                        type="hidden"
                        value={formData.customer_number}
                      />
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Name
                </th>
                <td>
                {user.role_name === 'SuperAdmin' ? (
                  <input
                    name="customer_name"
                    type="text"
                    className="data_entry_field"
                    value={formData.customer_name}
                    size="60"
                    onChange={handleChange}
                  />
                ) : (
                  <>
                  {formData.customer_name}
                  </>
                )}
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Home Page
                </th>
                <td>
                  <input
                    name="home_page"
                    type="text"
                    className="data_entry_field"
                    value={formData.home_page}
                    size="60"
                    onChange={handleChange}
                  />
                </td>
              </tr>
              {/*<tr>
                <th align="left" className="lbltxt" scope="row">
                  Limit Access
                </th>
                <td>
                  <input
                    name="limit_access"
                    type="text"
                    className="data_entry_field"
                    value={formData.limit_access}
                    size="60"
                    onChange={handleChange}
                  />
                </td>
              </tr>*/}
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Products
                </th>
                <td>
                  {user.role_name === 'SuperAdmin' ? (
                    // Editable for SuperAdmin
                    <div>
                      {Object.entries(PRODUCT_OPTIONS).map(([key, label]) => (
                        <label key={key} style={{ display: 'block' }}>
                          <input
                            type="checkbox"
                            value={key}
                            checked={hasProduct(key)}
                            onChange={handleProductChange}
                          />
                          {label}
                        </label>
                      ))}
                      <input
                        type="hidden"
                        name="products"
                        value={formData.products}
                      />
                    </div>
                  ) : (
                    // Read-only for Admin or others
                    <>
                      {formData.products
                        .split(',')
                        .map((key) => PRODUCT_OPTIONS[key.trim()])
                        .filter(Boolean)
                        .join(', ') || '(none)'}
                      <input
                        type="hidden"
                        name="products"
                        value={formData.products}
                      />
                    </>
                  )}
                </td>
              </tr>

              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Disable
                </th>
                <td>
                  {user.role_name === 'Admin' || user.role_name === 'SuperAdmin' ? (
                    <div>
                      {Object.entries(DISABLE_OPTIONS)
                        .filter(([key]) =>
                          formData.products
                            .split(',')
                            .map((v) => v.trim())
                            .includes(key)
                        )
                        .map(([key, label]) => (
                          <label key={key} style={{ display: 'block' }}>
                            <input
                              type="checkbox"
                              value={key}
                              checked={isDisabled(key)}
                              onChange={handleDisableChange}
                            />
                            {label}
                          </label>
                      ))}
                      {/* Hidden field for form submission */}
                      <input
                        type="hidden"
                        name="disable_list"
                        value={formData.disable_list}
                      />
                    </div>
                  ) : (
                    <>
                      {formData.disable_list}
                      <input
                        name="disable_list"
                        type="hidden"
                        value={formData.disable_list}
                      />
                    </>
                  )}
                </td>
              </tr>             
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Banner Graphic
                </th>
                <td>
                  {user.role_name === "Admin" || user.role_name === "SuperAdmin" ? (
                    <input
                      name="title_graphic"
                      type="text"
                      className="data_entry_field"
                      value={formData.title_graphic}
                      size="60"
                      onChange={handleChange}
                      onBlur={handleBlurTrim}
                    />
                  ) : (
                    <>
                      <span>{formData.title_graphic}</span>
                      <input
                        name="title_graphic"
                        type="hidden"
                        value={formData.title_graphic}
                      />
                    </>
                  )}
                </td>
              </tr>
              {(user.role_name === 'Admin' || user.role_name === 'SuperAdmin') && (
                <tr>
                  <th align="left" className="lbltxt" scope="row">
                    IP Access List
                  </th>
                  <td>
                    <input
                      name="ip_access_list"
                      type="text"
                      className="data_entry_field"
                      value={formData.ip_access_list}
                      size="60"
                      onChange={handleChange}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  County Number
                </th>
                <td>
                  {(user.role_name === 'SuperAdmin') ? (
                    <input
                      name="county_number"
                      type="text"
                      className="data_entry_field"
                      value={formData.county_number}
                      size="60"
                      onChange={handleChange}
                    />
                  ) : (
                    <>
                      {formData.county_number}
                      <input
                        name="county_number"
                        type="hidden"
                        value={formData.county_number}
                      />
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Accept Delinquent Payments for Current Year
                </th>
                <td style={{ paddingTop: '20px' }}>
                  <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                    <li>
                      <input
                        type="radio"
                        name="accept_delinquents"
                        value="1"
                        checked={formData.accept_delinquents === '1'}
                        onChange={handleChange}
                      />
                      <span>No delinquent payments (default)</span>
                    </li>
                    <li>
                      <input
                        type="radio"
                        name="accept_delinquents"
                        value="2"
                        checked={formData.accept_delinquents === '2'}
                        onChange={handleChange}
                      />
                      <span>
                        Ability to pay first half delinquent or full year
                        delinquent
                      </span>
                    </li>
                    <li>
                      <input
                        type="radio"
                        name="accept_delinquents"
                        value="3"
                        checked={formData.accept_delinquents === '3'}
                        onChange={handleChange}
                      />
                      <span>Pay full year delinquent</span>
                    </li>
                  </ul>
                </td>
              </tr>
              {(user.role_name === 'Admin' || user.role_name === 'SuperAdmin')  && (
                <>
                  <tr>
                    <th align="left" className="lbltxt" scope="row">
                      Allow for current year delinquents
                    </th>
                    <td>
                      <input
                        name="current_year_delinquents"
                        type="checkbox"
                        className="data_entry_field"
                        checked={!!formData.current_year_delinquents}
                        onChange={handleChange}
                      />
                    </td>
                  </tr>
                  {/*<tr>
                    <th align="left" className="lbltxt" scope="row">
                      Enable New Experience
                    </th>
                    <td>
                      <input
                        name="new_experience"
                        type="checkbox"
                        className="data_entry_field"
                        checked={formData.new_experience}
                        onChange={handleChange}
                      />
                    </td>
                  </tr>*/}
                  <tr>
                    <th align="left" className="lbltxt" scope="row">
                      Active
                    </th>
                    <td>
                      {user.role_name === 'SuperAdmin' ? (
                        <input
                          name="active"
                          type="text"
                          className="data_entry_field"
                          value={formData.active}
                          size="60"
                          onChange={handleChange}
                        />
                      ) : (
                        <span>{formData.active}</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th align="left" className="lbltxt" scope="row">
                      Client UID
                    </th>
                    <td>
                      {user.role_name === 'SuperAdmin' ? (
                        <input
                          name="uid"
                          type="text"
                          className="data_entry_field"
                          value={formData.uid}
                          size="60"
                          onChange={handleChange}
                        />
                      ) : (
                        <span>{formData.uid}</span>
                      )}
                    </td>
                  </tr>
                </>
              )}
              {(user.role_name === 'Admin' || user.role_name === 'SuperAdmin') && (
                <tr>
                  <th align="left" className="lbltxt" scope="row">
                    {formData.import_payment
                      ? 'Automatically Import Payments Into Tax'
                      : 'No Automatic Import of Payments'}
                  </th>
                  <td>  
                    <input
                      name="import_payment"
                      type="checkbox"
                      className="data_entry_field"
                      checked={formData.import_payment}
                      onChange={handleChange}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Tax ID Block List
                </th>
                <td>
                  <textarea
                    name="taxid_block_list"
                    className="data_entry_field"
                    style={{ width: '420px', height: '300px' }}
                    value={formData.taxid_block_list}
                    onChange={handleChange}
                  ></textarea>
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Welcome Message
                </th>
                <td>
                  <textarea
                    name="greeting"
                    className="data_entry_field"
                    style={{ width: '420px', height: '300px' }}
                    value={formData.greeting}
                    onChange={handleChange}
                  ></textarea>
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Tax Payment Message
                </th>
                <td>
                  <textarea
                    name="pay_tax_message"
                    className="data_entry_field"
                    style={{ width: '450px', height: '300px' }}
                    value={formData.pay_tax_message}
                    onChange={handleChange}
                  ></textarea>
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Disabled Message
                </th>
                <td>
                  <textarea
                    name="disable_message"
                    className="data_entry_field"
                    style={{ width: '450px', height: '300px' }}
                    value={formData.disable_message}
                    onChange={handleChange}
                  ></textarea>
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Tax Search Message
                </th>
                <td>
                  <textarea
                    name="tax_search_message"
                    className="data_entry_field"
                    style={{ width: '450px', height: '300px' }}
                    value={formData.tax_search_message}
                    onChange={handleChange}
                  ></textarea>
                </td>
              </tr>
              <tr>
                <th align="left" className="lbltxt" scope="row">
                  Payment Gateway
                </th>
                <td>
                  {user.role_name === 'SuperAdmin' ? (
                    <select
                      name="payment_gateway"
                      value={formData.payment_gateway}
                      onChange={handleChange}
                      className="data_entry_field"
                    >
                      <option value="">Select Payment Gateway</option>
                      <option value="certpay">Certified Payments</option>
                      <option value="gti">GovTeller / Intellipay</option>
                      <option value="nmi">Network Merchants</option>
                      <option value="nic">NIC/Montana Interactive</option> 
                      <option value="nue">Nuevie</option>                     
                      <option value="opi">Official Payments</option>
                    </select>
                  ) : (
                    <>
                      {paymentGatewayLabels[formData.payment_gateway] || formData.payment_gateway}
                      <input
                        name="payment_gateway"
                        type="hidden"
                        value={formData.payment_gateway}
                      />
                    </>
                  )}
                </td>
              </tr>

            </tbody>
          </table>
          <br />
          <input
            type="hidden"
            name="selected_customer"
            value={formData.customer_number}
          />
          {(user.role_name === 'Admin' || user.role_name === 'SuperAdmin') ? (
            <input
              type="submit"
              name="btnUpdate_Customer"
              value="Update Customer"
              id="FormsButton2"
            />
          ) : (
            <input
              type="submit"
              name="btnUpdate_Customer"
              value="Update"
              id="FormsButton2"
            />
          )}
          <input
            type="hidden"
            name="customer_id"
            value={formData.customer_number}
          />
          <input
            type="hidden"
            name="header_text"
            value="Manage Site"
          />
          <input
            type="hidden"
            name="body_include"
            value="manage_customer"
          />
          <input
            type="hidden"
            name="current_app"
            value="tax"
          />
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
